import isEqual from 'underscore/modules/isEqual';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import mapboxgl from 'mapbox-gl';
import { decodeSegmentLngLat, getLineColor, getSegments } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import { routePoints } from './routing';
import { encodeSegmentLngLat, lngLatBoundsToAtBounds, lngLatToAtLocation, buildAtMap } from '../at_map_helpers';
import { RoutePolyline } from './route_polyline';
import { RouteNode } from './route_node';

class TrailPlanner {
  constructor({ onRouteInfoChange, route, routingActive, routingMode = 'hiking', updateProgressDialog }) {
    this.hasChanged = false; // Flag for whether or not the route has been modified
    this.onRouteInfoChange = onRouteInfoChange; // Called on checkRoute()
    this.route = route;
    this.color = getLineColor(route);
    this.routingActive = routingActive; // Flag for smart-routing
    this.routingMode = routingMode; // Transit-mode for smart-routing
    this.routeStart = null; // lngLatLike
    this.routePolylines = []; // [RoutePolyline] - All of the edges composing a new route
    this.routingInProgress = false;
    this.routeNodes = []; // [RouteNode] - All of the nodes composing a new route
    this.undoStack = []; // [[ RouteState ]]
    this.redoStack = []; // [[ RouteState ]]
    this.updateProgressDialog = updateProgressDialog;
  }

  initRoutePolylines() {
    const segments = getSegments(this.route);
    if (segments.length < 1) {
      this.checkRoute();
      return;
    }
    segments.forEach(segment => {
      this.addPolyline(decodeSegmentLngLat(segment));
    });
  }

  outAndBack() {
    // Turn into "out and back" with simple retrace along current path.

    // Do nothing if no line, or if end point is already the same as the start point:
    if (this.routePolylines.length < 1) {
      return;
    }
    const startPt = this.getEndPt(0);
    const endPt = this.getEndPt(1);
    if (isEqual(startPt, endPt)) {
      return;
    }

    for (let i = this.routePolylines.length - 1; i >= 0; i -= 1) {
      // ignore start point
      this.routePolylines.push(this.routePolylines[i].getCopy(true));
    }
    this.checkRoute();
    this.updateUndoRedoStacks();
  }

  reverseRoute() {
    // Do nothing if no line
    if (this.routePolylines.length < 1) {
      return;
    }

    const reversed = [];
    for (let i = this.routePolylines.length - 1; i >= 0; i -= 1) {
      reversed.push(this.routePolylines[i].getCopy(true));
    }
    this.routePolylines = reversed;

    this.hasChanged = true;
    this.checkRoute();
    this.updateUndoRedoStacks();
  }

  returnToStart() {
    // Try to route back to start through trail network.

    // Do nothing if no line, or if end point is already the same as the start point:
    if (this.routePolylines.length < 1) {
      return;
    }
    const startPt = this.getEndPt(0);
    const endPt = this.getEndPt(1);
    if (isEqual(startPt, endPt)) {
      return;
    }

    // Route back to start point
    this.routeToPoint(startPt);
  }

  routeToPoint(lngLat, ignoreOrigEnd = false) {
    // Skip this point if routing to another point is still in progress to prevent using a stale endpoint
    if (this.routingInProgress) {
      return;
    }

    this.routingInProgress = true;
    if (!this.routingActive || !this.routeStart) {
      this.addPoint(lngLat);
      this.routingInProgress = false;
      return;
    }
    const endPt = this.getEndPt(1);
    return routePoints(this.routingMode, [endPt, lngLat], false, ignoreOrigEnd)
      .then(points => {
        this.routePolylines.push(new RoutePolyline(points));
        // notify rest of system that change has been made to route
        this.hasChanged = true;
        this.checkRoute();
        this.updateUndoRedoStacks();
        this.routingInProgress = false;
      })
      .catch(err => {
        this.handleRoutingError(err);
        this.routingInProgress = false;
      });
  }

  pointsAsPromise(pts, ignoreOrigStart, ignoreOrigEnd) {
    if (!this.routingActive) {
      return Promise.resolve(pts);
    }
    return routePoints(this.routingMode, pts, ignoreOrigStart, ignoreOrigEnd).catch(err => this.handleRoutingError(err));
  }

  handleRoutingError(error) {
    if (!this.updateProgressDialog) {
      return;
    }
    // Error code of 442 is returned by Valhalla when it is impossible to route to a specific point (e.g. no graph edges reach the destination)
    if (error && error.error_code == 442) {
      this.updateProgressDialog('error-cannot-route');
    } else {
      this.updateProgressDialog('error-routing');
    }
  }

  addPoint(lngLat) {
    this.hasChanged = true;
    if (!this.routeStart) {
      this.routeStart = lngLat;
    } else {
      this.addGapFillIfNeeded(lngLat);
    }
    this.checkRoute();
    this.updateUndoRedoStacks();
  }

  addPolyline(points) {
    this.hasChanged = true;
    if (!this.routeStart) {
      this.routeStart = points[0];
    } else {
      this.addGapFillIfNeeded(points[0]);
    }
    this.routePolylines.push(new RoutePolyline(points));
    this.checkRoute();
    this.updateUndoRedoStacks();
  }

  addGapFillIfNeeded(lngLat) {
    const endPt = this.getEndPt(1);
    if (!isEqual(endPt, lngLat)) {
      this.routePolylines.push(new RoutePolyline([endPt, lngLat]));
    }
  }

  // used for dragging to draw polyline
  startDynamicPolyline(lngLat) {
    const points = [];
    const endPt = this.getEndPt(1);
    if (endPt) {
      points.push(endPt);
    }
    if (lngLat) {
      points.push(lngLat);
    }
    this.routePolylines.push(new RoutePolyline(points));
    this.checkRoute(false);
  }

  endDynamicPolyline(lngLat) {
    this.hasChanged = true;
    const dynamicPolyline = this.routePolylines[this.routePolylines.length - 1];
    const points = dynamicPolyline.points;
    if (lngLat) {
      points.push(lngLat);
    }
    if (dynamicPolyline && dynamicPolyline.points.length < 2) {
      this.routePolylines.pop();
    } else {
      this.updateUndoRedoStacks();
    }
    this.checkRoute();
  }

  updateDynamicPolyline(lngLat) {
    const dynamicPolyline = this.routePolylines[this.routePolylines.length - 1];
    const points = dynamicPolyline.points;
    points.push(lngLat);
    if (points.length > 1) {
      this.hasChanged = true;
      this.checkRoute(false);
    }
  }

  updateUndoRedoStacks() {
    this.updateUndoStack();
    this.updateRedoStack();
  }

  updateUndoStack() {
    this.undoStack.push({
      routeStart: this.routeStart ? this.routeStart.slice() : null,
      routePolylines: this.routePolylines.slice()
    });
  }

  updateRedoStack() {
    this.redoStack = [];
  }

  updateRouteState(routeState) {
    this.routeStart = routeState.routeStart ? routeState.routeStart.slice() : null;
    this.routePolylines = routeState.routePolylines.slice();
  }

  undo() {
    const undoStack = this.undoStack;
    if (undoStack.length > 0) {
      this.hasChanged = true;
      // remove state from undo stack and tack onto redoStack
      this.redoStack.push(this.undoStack.pop());
      // update routePolylines to last state's routePolylines
      const routeState = undoStack[undoStack.length - 1] || { routePolylines: [], routeStart: null };
      this.updateRouteState(routeState);
      // update rendering of route
      this.checkRoute();
    }
  }

  redo() {
    const redoStack = this.redoStack;
    if (redoStack.length > 0) {
      this.hasChanged = true;
      // remove state from redoStack
      const routeState = redoStack.pop();
      // update state to that of popped state
      this.updateRouteState(routeState);
      // move popped state back onto undoStack
      this.updateUndoStack();
      // update rendering of route
      this.checkRoute();
    }
  }

  checkRoute(triggerFullChange = true) {
    let distance = 0.0;
    const routeNodes = [];
    const routePolylines = this.routePolylines;
    routePolylines.forEach((routePolyline, i) => {
      distance += routePolyline.getLength();
      const endPts = routePolyline.getEndPts();
      routeNodes.push(new RouteNode(i, endPts[0]));
      if (i === routePolylines.length - 1) {
        routeNodes.push(new RouteNode(i + 1, endPts[1]));
      }
    });
    if (routeNodes.length < 1) {
      routeNodes.push(new RouteNode(0, this.routeStart));
    }
    this.routeNodes = routeNodes;

    const routeStartPt = this.getEndPt(0);
    const routeEndPt = this.getEndPt(1);
    const endsCoincide = isEqual(routeStartPt, routeEndPt);
    this.onRouteInfoChange({
      distance: distance * 1000, // km -> m
      undo_available: this.undoStack.length > 0,
      redo_available: this.redoStack.length > 0,
      rtsAvailable: !endsCoincide,
      routeStartPt,
      routeEndPt,
      endsCoincide,
      fullChange: triggerFullChange,
      hasChanged: this.hasChanged,
      plannerMap: this.toAtMap()
    });
  }

  getEndPt(end) {
    if (this.routePolylines.length === 0) {
      return this.routeStart;
    }
    const routeItem = end === 0 ? this.routePolylines[0] : this.routePolylines[this.routePolylines.length - 1];
    if (!routeItem) {
      return null;
    }
    return routeItem.getEndPt(end);
  }

  routeNodeMoved(node, lngLat) {
    let spliceAt = node.idx;
    const toAddPromises = [];
    if (node.idx === 0) {
      this.routeStart = lngLat;
    }
    const prevPolyline = this.routePolylines[node.idx - 1];
    if (prevPolyline) {
      spliceAt -= 1;
      toAddPromises.push(this.pointsAsPromise([prevPolyline.getEndPt(0), lngLat], false, true));
    }
    const nextPolyline = this.routePolylines[node.idx];
    if (nextPolyline) {
      toAddPromises.push(this.pointsAsPromise([lngLat, nextPolyline.getEndPt(1)], true, false));
    }
    const toRemove = [prevPolyline, nextPolyline].filter(p => !!p);
    this.updatePolylinesHelper(spliceAt, toRemove, toAddPromises);
  }

  routeNodeRemoved(node) {
    let spliceAt = node.idx;
    const toAddPromises = [];
    const prevPolyline = this.routePolylines[node.idx - 1];
    if (prevPolyline) {
      spliceAt -= 1;
      if (node.idx === 1) {
        this.routeStart = prevPolyline.getEndPt(0);
      }
    }
    const nextPolyline = this.routePolylines[node.idx];
    if (nextPolyline && node.idx === 0) {
      this.routeStart = nextPolyline.getEndPt(1);
    }
    if (prevPolyline && nextPolyline) {
      toAddPromises.push(this.pointsAsPromise([prevPolyline.getEndPt(0), nextPolyline.getEndPt(1)]));
    } else if (!prevPolyline && !nextPolyline && node.idx === 0) {
      this.routeStart = null;
    }
    const toRemove = [prevPolyline, nextPolyline].filter(p => !!p);
    this.updatePolylinesHelper(spliceAt, toRemove, toAddPromises);
  }

  updatePolylinesHelper(spliceAt, toRemove, toAddPromises) {
    return Promise.all(toAddPromises).then(values => {
      const toAdd = values.map(points => new RoutePolyline(points));
      this.routePolylines.splice(spliceAt, toRemove.length, ...toAdd);
      this.hasChanged = true;
      this.checkRoute();
      this.updateUndoRedoStacks();
    });
  }

  splitRouteAtNewNode(newRouteNode) {
    const closestIdx = newRouteNode.cropIdx;
    // Safety-first!
    if (!closestIdx && closestIdx !== 0) {
      return;
    }
    // Split line either just before or just after closest index
    const polyline = this.routePolylines[newRouteNode.polylineIdx];
    const points = polyline.points;
    let firstHalf;
    let secondHalf;
    if (closestIdx === points.length - 1) {
      // break just before closest index
      firstHalf = points.slice(0, closestIdx); // end first half with prev
      secondHalf = points.slice(closestIdx, points.length); // start second half with closest
    } else {
      // break just after closest index
      firstHalf = points.slice(0, closestIdx + 1); // end first half with closest
      secondHalf = points.slice(closestIdx + 1, points.length); // start second half with next
    }
    // Push/unshift interpolated point onto split halves
    firstHalf.push(newRouteNode.lngLat);
    secondHalf.unshift(newRouteNode.lngLat);
    // Replace polyline with new splits
    this.routePolylines.splice(newRouteNode.polylineIdx, 1, new RoutePolyline(firstHalf), new RoutePolyline(secondHalf));
    // Update state
    this.hasChanged = true;
    this.checkRoute();
    this.updateUndoRedoStacks();
  }

  toAtMap(mapConfig = {}) {
    // Build API compatible map for route
    // Based on data_manager.toJSON()
    const map = buildAtMap(
      {
        name: 'Trail Planner Map',
        ...mapConfig
      },
      <FormattedMessage defaultMessage="Map" />
    );

    const mapBounds = new mapboxgl.LngLatBounds();

    if (this.routePolylines.length > 0) {
      const color = this.color;
      let sequenceNum = 1;
      const lineSegments = this.routePolylines.map(routeItem => {
        const points = routeItem.getPts();
        points.forEach(pt => mapBounds.extend(pt));
        const lineSegment = encodeSegmentLngLat(points, sequenceNum);
        sequenceNum += 1;
        return lineSegment;
      });
      map.routes = [{ lineDisplayProperty: { color }, lineSegments, sequence_num: 1 }];
    }

    if (!mapBounds.isEmpty()) {
      map.bounds = lngLatBoundsToAtBounds(mapBounds);
      map.location = lngLatToAtLocation(mapBounds.getCenter());
    }

    return map;
  }
}

export { TrailPlanner };
